import { useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useUniqueTableName from '../../../hooks/useUniqueTableName';
import useBaseTableProps from '../../../hooks/useBaseTableProps';
import useActionsFirstColumnOrder from '../../../hooks/useActionsFirstColumnOrder';
import { processTableFilterOptions } from '../../../utils/table-helpers';
import { HEADERS, PROPERTY_PATH_MAP } from './constants';
import messages from './messages';
import parentMessages from '../messages';
import ApprovedAmountCell from '../ApprovedAmountCell';
import { VARIANCE_CELL_SX } from '../constants';
import {
  canDelete,
  getFirstMatchingReason,
  canEditApprovedAmount,
} from '../utils';

function useBuildFilterOptions({ results }) {
  return useMemo(() => {
    const options = (results || []).reduce(
      (acc, result) => {
        if (result.statusDisplay) acc.status.push(result.statusDisplay);
        if (result.requestTypeDisplay)
          acc.requestType.push(result.requestTypeDisplay);
        if (result.chargeCode) acc.chargeCode.push(result.chargeCode);
        return acc;
      },
      {
        status: [],
        requestType: [],
        chargeCode: [],
      },
    );
    processTableFilterOptions(options);
    return options;
  }, [results]);
}

function useHeaders({ results }) {
  const filterOptions = useBuildFilterOptions({ results });

  return useMemo(() => {
    return HEADERS.map((header) => ({
      ...header,
      filterOptions: header.filterOptions ?? filterOptions[header.id],
    }));
  }, [filterOptions]);
}

function useRows({
  isReadOnly,
  miscellaneousAccounting,
  onDelete,
  voucherSubmissionStatus,
  hasVariance,
  onSaveApprovedAmount,
  canManage,
}) {
  return useMemo(() => {
    return miscellaneousAccounting.map((row) => {
      const isPermissionDisabled = !canManage;
      const isDeleteDisabled =
        isPermissionDisabled ||
        !onDelete ||
        !canDelete(row.status, voucherSubmissionStatus) ||
        isReadOnly;

      return {
        id: row.id,
        requestType: {
          value: row.requestType,
        },
        comments: {
          variant: 'description',
          value: row.comments,
        },
        requestedAmount: {
          variant: 'currency',
          numberFormatOptions: { minimumFractionDigits: 2 },
          value: row.requestedAmount,
        },
        approvedAmount: canEditApprovedAmount(
          hasVariance,
          isReadOnly,
          canManage,
        )
          ? {
              sx: VARIANCE_CELL_SX,
              value: (
                <ApprovedAmountCell
                  key={row.id}
                  initialValue={row.approvedAmount}
                  onSave={(amount) => onSaveApprovedAmount?.(row, amount)}
                />
              ),
            }
          : {
              variant: 'currency',
              numberFormatOptions: { minimumFractionDigits: 2 },
              value: row.approvedAmount,
            },
        chargeCode: {
          value: row.chargeCode,
        },
        actions: {
          variant: 'menu',
          name: 'actions',
          iconName: 'BoltIcon',
          color: 'primary',
          closeOnClick: true,
          options: [
            {
              value: 'delete',
              text: <FormattedMessage {...messages.delete} />,
              disabled: isDeleteDisabled,
              tooltip: getFirstMatchingReason([
                {
                  condition: isPermissionDisabled,
                  message: (
                    <FormattedMessage {...parentMessages.noPermissionTooltip} />
                  ),
                },
              ]),
              onClick: !isDeleteDisabled ? () => onDelete(row) : null,
            },
          ],
        },
      };
    });
  }, [
    miscellaneousAccounting,
    onDelete,
    voucherSubmissionStatus,
    hasVariance,
    isReadOnly,
    onSaveApprovedAmount,
    canManage,
  ]);
}

export function useMiscellaneousAccountingTable({
  isReadOnly,
  miscellaneousAccounting = [],
  onDelete,
  voucherSubmissionStatus,
  hasVariance = false,
  onSaveApprovedAmount,
  canManage,
}) {
  const name = useUniqueTableName('miscellaneous-accounting-table');
  const baseTableProps = useBaseTableProps();

  const headers = useHeaders({ results: miscellaneousAccounting });

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers,
  });

  const sortedAndFilteredResults = useTableFilterSortData({
    results: miscellaneousAccounting,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  const handleDelete = useCallback(
    (row) => {
      if (onDelete) onDelete(row);
    },
    [onDelete],
  );

  const rows = useRows({
    isReadOnly,
    miscellaneousAccounting: sortedAndFilteredResults,
    onDelete: handleDelete,
    voucherSubmissionStatus,
    hasVariance,
    onSaveApprovedAmount,
    canManage,
  });

  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  return {
    baseTableProps,
    name,
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    filterState,
    filterTypeState,
    dateState,
    searchState,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    isLoading: false,
    order,
    orderBy,
    rows,
    count: rows.length,
    totalCount: miscellaneousAccounting.length,
  };
}
